import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetProductResponse } from '@/api/generated/schemas';

/**
 * UI hodnota destinace ve formuláři – drží bohatý objekt kvůli renderu chipu, ne jen id.
 * Do API příkazu se transformuje na `destinationId` (viz `toProductDetailRequest`).
 * Shodný tvar s destinacemi projektu.
 */
const destinationOptionSchema = z.object({
  destinationId: z.number(),
  name: z.string(),
  code: z.string().nullish(),
});

export type DestinationOption = z.infer<typeof destinationOptionSchema>;

/**
 * Factory pro Zod schéma detailu produktu. Lokalizované hlášky se vkládají přímo do schématu
 * (single source of truth), proto se schéma staví z `t` až za běhu.
 */
export function buildProductDetailFormSchema(t: TFunction) {
  return z.object({
    name: z.string().trim().min(1, t('productDetail.validation.nameRequired')),
    destinations: z.array(destinationOptionSchema),
  });
}

export type ProductDetailFormValues = z.infer<ReturnType<typeof buildProductDetailFormSchema>>;

/** Prázdné výchozí hodnoty (fallback, než dorazí detail z API). */
export function emptyProductDetailFormValues(): ProductDetailFormValues {
  return {
    name: '',
    destinations: [],
  };
}

/** Mapuje detail produktu z API na hodnoty formuláře. */
export function fromGetProductResponse(response: GetProductResponse): ProductDetailFormValues {
  return {
    name: response.name,
    destinations: response.destinations.map((destination) => ({
      destinationId: destination.destinationId,
      name: destination.name,
      code: destination.code,
    })),
  };
}
